import { DateTimeFormatter, Instant, ZoneId } from '@js-joda/core';
import { FieldTypeCategory, isParameterTyped, simplifyType } from '@thinkalpha/language-services';
import { generateExportTimestamp, saveFile } from '@thinkalpha/ui-core/util/csv';
import type { TFunction } from 'i18next';
import { unparse } from 'papaparse';
import type { TableModel } from 'src/models/TableModel';
import type { ToastService } from 'src/services/ToastService';

const excelDateTimeFormatter = DateTimeFormatter.ofPattern('yyyy-MM-dd HH:mm:ss');

export type ExportTableCSVResult =
    | { success: true }
    | {
          success: false;
          error: 'no-table' | 'no-columns' | 'no-data' | 'aborted' | 'unavailable' | 'unknown';
          message?: string;
      };

/** The table surface an export reads. */
export type ExportableTable = Pick<TableModel, 'columns' | 'snapshot'>;

/**
 * Exports a table to CSV format and triggers a file download.
 * This is a pure utility function with no React dependencies, suitable for use in model layers.
 *
 * @param table - The table model to export
 * @param columnNames - Map of column IDs to display names (only columns in this map will be exported)
 * @param signal - Aborting it before the snapshot arrives skips the download
 * @returns Result indicating success or failure with error type
 */
export async function exportTableToCSV(
    table: ExportableTable | undefined,
    columnNames: Record</* id */ string, /* name */ string>,
    t: TFunction,
    { signal }: { signal?: AbortSignal } = {},
): Promise<ExportTableCSVResult> {
    if (!table) {
        return { success: false, error: 'no-table' };
    }

    try {
        const columns = table.columns;
        if (!columns) {
            return { success: false, error: 'no-columns' };
        }

        // Get all rows (max 1 million for practical limits)
        const snapshot = await table.snapshot({ firstRow: 0, windowSize: 1000000 });
        if (signal?.aborted) {
            return { success: false, error: 'aborted' };
        }

        if (!snapshot?.data?.length) {
            return { success: false, error: 'no-data' };
        }

        const rows: Record<string, unknown>[] = [];
        for (const row of snapshot.data) {
            let newRow: Record<string, unknown> = {};
            for (const col of columns) {
                const key = col.id;
                // Only include columns that are in the columnNames map
                if (!columnNames[key]) continue;
                let value = row[key];
                // Format timestamps for Excel compatibility
                if (
                    !isParameterTyped(col.dataType) &&
                    simplifyType(col.dataType) === FieldTypeCategory.Timestamp &&
                    typeof value === 'number'
                ) {
                    const instant = Instant.ofEpochMicro(Math.round(value * 1000));
                    const zoned = instant.atZone(ZoneId.UTC);
                    value = excelDateTimeFormatter.format(zoned);
                }
                newRow = {
                    ...newRow,
                    [columnNames[key]]: value,
                };
            }
            rows.push(newRow);
        }

        const csv = unparse(rows);
        const blob = new Blob([csv], { type: 'text/csv' });
        const filename = t('widgets::export::csv-filename', { timestamp: generateExportTimestamp() });
        saveFile(`${filename}.csv`, blob, 'text/csv');

        return { success: true };
    } catch (e) {
        return {
            success: false,
            error: 'unknown',
            message: e instanceof Error ? e.message : String(e),
        };
    }
}

/**
 * How long the "beginning export" toast may stay open. The export dismisses it once it settles;
 * this only closes it if the export never does. It outlives a stitched export's construction wait.
 */
export const EXPORT_PENDING_TOAST_MS = 60_000;

/**
 * Runs an export with toasts: a pending toast while it runs, then one for its outcome. An aborted
 * export (superseded, or its widget closed) shows no outcome, and a missing table shows none, as
 * before the model owned the export.
 */
export async function exportWithToasts(
    toasts: ToastService,
    t: TFunction,
    run: () => Promise<ExportTableCSVResult>,
): Promise<ExportTableCSVResult> {
    const pending = toasts.show({
        message: t('widgets::table-widgets::table-view::export::export-table-begin'),
        durationMs: EXPORT_PENDING_TOAST_MS,
    });
    let result: ExportTableCSVResult;
    try {
        result = await run();
    } catch (error) {
        result = { success: false, error: 'unknown', message: error instanceof Error ? error.message : String(error) };
    } finally {
        toasts.dismiss(pending);
    }

    if (result.success) {
        toasts.show({
            message: t('widgets::table-widgets::table-view::export::export-table-success'),
            variant: 'success',
        });
    } else if (result.error === 'no-data') {
        toasts.show({
            message: t('widgets::table-widgets::table-view::export::export-table-error-no-data'),
            variant: 'warning',
        });
    } else if (result.error === 'no-columns') {
        toasts.show({
            message: t('widgets::table-widgets::table-view::export::export-table-error-no-columns'),
            variant: 'error',
        });
    } else if (result.error === 'unknown') {
        toasts.show({
            message: t('widgets::table-widgets::table-view::export::export-table-error'),
            variant: 'error',
            debugMessage: result.message,
        });
    }
    return result;
}
